import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import FinancingOwnerSnapshotView from '../FinancingCalculator/FinancingOwnerSnapshotView';
import ValuationSnapshotResult from '../ValuationStudio/ValuationSnapshotResult';
import DealStructureResults from '../DealStructure/DealStructureResults';

// Adapts a saved snapshot's `inputs` + `results` JSON to the prop shape each
// owner-facing component expects. Most components want { snapshot } directly;
// keeping a per-type adapter so we can reshape when needed.
function buildFinancingProps(snapshot) {
  return { snapshot };
}

function buildValuationProps(snapshot) {
  // ValuationSnapshotResult takes `result` — a single object containing both
  // the calculated outputs and the inputs. The container builds this from
  // the GraphQL valuation mutation response. Our snapshot.results is the
  // same shape (with snapshot.inputs available alongside).
  const result = {
    ...(snapshot?.results || {}),
    inputs: snapshot?.inputs || {},
  };

  return {
    result,
    readOnly: true,
    bare: true,
  };
}

function buildDealStructureProps(snapshot) {
  return {
    result: snapshot?.results || {},
    inputs: snapshot?.inputs || {},
    readOnly: true,
  };
}

const COMPONENT_BY_TYPE = {
  valuation_snapshot: { Component: ValuationSnapshotResult, propsBuilder: buildValuationProps },
  financing_snapshot: { Component: FinancingOwnerSnapshotView, propsBuilder: buildFinancingProps },
  deal_structure_snapshot: { Component: DealStructureResults, propsBuilder: buildDealStructureProps },
};

const OwnerViewModal = ({ open, onClose, snapshot, snapshotType }) => {
  // Lock body scroll while the modal is open so the underlying expert view
  // doesn't scroll behind the modal scroll.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const config = COMPONENT_BY_TYPE[snapshotType];
  if (!config) return null;

  const { Component, propsBuilder } = config;
  const componentProps = propsBuilder(snapshot);

  return (
    // Backdrop click closes the modal (UX convention). Esc handler covers
    // keyboard close; the visible X button is fully keyboard-accessible.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-stretch bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="owner-view-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full bg-[hsl(var(--background))] overflow-y-auto">
        {/* Top bar with title + close. Sticky so the close button is always
            reachable while scrolling through long results panels. */}
        <div className="sticky top-0 z-10 bg-[hsl(var(--background))] border-b border-[hsl(var(--border))] px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-wide text-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/10 px-2 py-0.5 rounded">
              OWNER&rsquo;S VIEW
            </span>
            <h2
              id="owner-view-modal-title"
              className="font-serif text-[16px] font-light text-[hsl(var(--foreground))]"
            >
              What the owner sees in their {snapshotType?.replace(/_snapshot$/, '').replace(/_/g, ' ')} calculator
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close owner view"
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — host the owner-facing component. Each component handles
            its own padding/max-width so we don't double-wrap. Prop-spreading
            is the dispatcher pattern: each owner component takes a different
            shape and we adapt the snapshot data via `propsBuilder`. */}
        <div className="bg-[hsl(var(--background))]">
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <Component {...componentProps} />
        </div>
      </div>
    </div>
  );
};

OwnerViewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  snapshot: PropTypes.shape({
    inputs: PropTypes.object,
    results: PropTypes.object,
  }),
  snapshotType: PropTypes.string.isRequired,
};

OwnerViewModal.defaultProps = {
  snapshot: null,
};

export default OwnerViewModal;
